import React, { useState, useEffect, useRef } from "react";
import { Box, Typography, Divider } from "@mui/material";

const CountUpStat = ({
  number,
  label,
  textColor,
  accentColor,
  startCount,
  duration,
}) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!startCount) return;
    let start = 0;
    const end = parseInt(number, 10);
    if (start === end) return;

    let startTime = null;

    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / duration, 1);
      const current = Math.floor(progress * (end - start) + start);
      setCount(current);
      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };
    requestAnimationFrame(animate);
  }, [startCount, number, duration]);

  const formatNumber = (num) => {
    if (num >= 1_000_000_000)
      return (
        (num / 1_000_000_000).toFixed(num % 1_000_000_000 === 0 ? 0 : 1) + "B"
      );
    if (num >= 1_000_000)
      return (num / 1_000_000).toFixed(num % 1_000_000 === 0 ? 0 : 1) + "M";
    if (num >= 1_000)
      return (num / 1_000).toFixed(num % 1_000 === 0 ? 0 : 1) + "K";
    return num.toString();
  };

  return (
    <Box sx={{ textAlign: "center", flex: 1 }}>
      <Typography
        sx={{
          fontSize: { xs: "2.2rem", md: "3.5rem" },
          fontWeight: "bold",
          color: textColor,
          marginBottom: "8px",
          "& .plus": { color: accentColor },
        }}
      >
        {formatNumber(count)}
        <span className="plus">+</span>
      </Typography>
      <Typography
        sx={{
          fontSize: { xs: "1rem", md: "1.1rem" },
          color: textColor,
          fontWeight: 400,
          maxWidth: { xs: "80%", md: "100%" },
          margin: "0 auto",
        }}
      >
        {label}
      </Typography>
    </Box>
  );
};

const Numbers = ({
  statistics,
  backgroundColor,
  textColor,
  accentColor,
  duration = 2000,
}) => {
  const bg = backgroundColor || "#2a2a2a";
  const txtColor = textColor || "#ffffff";
  const accColor = accentColor || "#ff6b35";

  const [startCount, setStartCount] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setStartCount(true);
          observer.disconnect();
        }
      },
      { threshold: 0.3 }
    );
    if (containerRef.current) {
      observer.observe(containerRef.current);
    }
    return () => observer.disconnect();
  }, []);

  return (
    <Box
      ref={containerRef}
      sx={{
        backgroundColor: bg,
        padding: { xs: "40px 20px", md: "60px 40px" },
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Decorative Lines */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "120px",
          height: "120px",
          "&::before, &::after": {
            content: '""',
            position: "absolute",
            backgroundColor: accColor,
            height: "3px",
            width: "80px",
          },
          "&::before": {
            top: "20px",
            left: "20px",
            transform: "rotate(45deg)",
          },
          "&::after": {
            top: "35px",
            left: "20px",
            transform: "rotate(45deg)",
          },
          "@media (max-width: 768px)": {
            width: "80px",
            height: "80px",
            "&::before": { width: "50px" },
            "&::after": { width: "50px", top: "25px" },
          },
        }}
      />

      {/* Stats Grid */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          maxWidth: "1200px",
          margin: "0 auto",
          gap: { xs: "20px", md: "40px" },
          flexDirection: { xs: "column", md: "row" },
        }}
      >
        {statistics.map((stat, index) => (
          <React.Fragment key={index}>
            <CountUpStat
              number={stat.number}
              label={stat.label}
              textColor={txtColor}
              accentColor={accColor}
              startCount={startCount}
              duration={duration}
            />
            {index < statistics.length - 1 && (
              <Divider
                orientation="vertical"
                flexItem
                sx={{
                  backgroundColor: accColor,
                  width: "2px",
                  height: "80px",
                  display: { xs: "none", md: "block" },
                }}
              />
            )}
          </React.Fragment>
        ))}
      </Box>
    </Box>
  );
};

export default Numbers;
